import { exchangeWallTimeToUtcMs } from '@/lib/exchange-time'

/**
 * Replay start-time vocabulary, shared by the calendar's Replay Mode section
 * and every entry point that has to turn "a day" into "an instant".
 *
 * All times are EXCHANGE wall time (America/New_York), resolved through
 * `exchangeWallTimeToUtcMs` so DST is never hand-rolled.
 *
 * Spec: docs/superpowers/specs/2026-08-27-chart-replay-calendar-design.md
 */

export type ReplayTimePresetId =
  | 'pre-market'
  | 'open'
  | 'mid-morning'
  | 'power-hour'
  | 'after-hours'
  | 'custom'

export interface ReplayTimePreset {
  id: Exclude<ReplayTimePresetId, 'custom'>
  label: string
  /** What the preset actually means, in exchange wall time. */
  hour: number
  minute: number
}

/**
 * The trading day's named moments, in session order.
 *
 * `pre-market` is 4:00 AM, the pre-market session open. Replaying a small-cap
 * runner from 9:30 skips the move that made it worth replaying, which is the
 * complaint this whole feature answers.
 *
 * `mid-morning` is 10:30, after the opening drive has resolved, where traders
 * rehearsing continuation rather than the open want to pick up.
 *
 * `power-hour` is 2:00 PM per the product call. Note this is earlier than the
 * textbook 3:00–4:00 power hour; it is deliberate, so the preset lands before
 * the afternoon move rather than inside it.
 *
 * The label plus its clock time is the whole option: an explanatory sub-line
 * per row made the column read as a wall of grey text.
 */
export const REPLAY_TIME_PRESETS: readonly ReplayTimePreset[] = [
  { id: 'pre-market', label: 'Pre-market', hour: 4, minute: 0 },
  { id: 'open', label: 'Open', hour: 9, minute: 30 },
  { id: 'mid-morning', label: 'Mid-morning', hour: 10, minute: 30 },
  { id: 'power-hour', label: 'Power hour', hour: 14, minute: 0 },
  { id: 'after-hours', label: 'After Hours', hour: 16, minute: 0 },
] as const

export interface ReplayTimeSelection {
  preset: ReplayTimePresetId
  /** Free-entry time, only meaningful when `preset === 'custom'`. `HH:mm`. */
  custom: string
}

export const DEFAULT_REPLAY_TIME: ReplayTimeSelection = { preset: 'open', custom: '09:30' }

const OPEN = REPLAY_TIME_PRESETS.find((p) => p.id === 'open')!

const CLOCK_RE = /^\s*(\d{1,2})\s*:\s*(\d{2})\s*(am|pm)?\s*$/i

/**
 * Parse a user-typed clock time. Accepts `9:30`, `09:30`, `16:00`, `4:00 PM`,
 * `4:00pm`. Returns null for anything out of range: the caller falls back to
 * the regular open rather than starting a replay at a nonsense instant.
 */
export function parseClockText(text: string): { hour: number; minute: number } | null {
  const m = CLOCK_RE.exec(text ?? '')
  if (!m) return null
  let hour = Number(m[1])
  const minute = Number(m[2])
  const meridiem = m[3]?.toLowerCase()
  if (minute > 59) return null
  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    if (meridiem === 'pm') hour = hour === 12 ? 12 : hour + 12
    else hour = hour === 12 ? 0 : hour
  } else if (hour > 23) {
    return null
  }
  return { hour, minute }
}

/**
 * Render exchange wall time as 24-hour `04:00`, `09:30`, `16:00`, the one
 * clock format the app uses, matching every stored value and every time input.
 */
export function formatClockText(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/** The wall time a selection resolves to, falling back to the regular open. */
function clockOf(selection: ReplayTimeSelection): { hour: number; minute: number } {
  if (selection.preset === 'custom') {
    return parseClockText(selection.custom) ?? { hour: OPEN.hour, minute: OPEN.minute }
  }
  const preset = REPLAY_TIME_PRESETS.find((p) => p.id === selection.preset)
  return preset
    ? { hour: preset.hour, minute: preset.minute }
    : { hour: OPEN.hour, minute: OPEN.minute }
}

/** Human label for a selection: `Open · 09:30`, or just `07:15` for custom. */
export function replayTimeLabel(selection: ReplayTimeSelection): string {
  const { hour, minute } = clockOf(selection)
  const clock = formatClockText(hour, minute)
  if (selection.preset === 'custom') return clock
  const preset = REPLAY_TIME_PRESETS.find((p) => p.id === selection.preset)
  return preset ? `${preset.label} · ${clock}` : clock
}

/**
 * The custom time as parts, for the hour/minute dropdowns. Falls back to the
 * regular open so the pickers always have a concrete value to show.
 */
export function customClockParts(selection: ReplayTimeSelection): {
  hour: number
  minute: number
} {
  return parseClockText(selection.custom) ?? { hour: OPEN.hour, minute: OPEN.minute }
}

/** Serialize dropdown parts back into the stored `HH:mm` form. */
export function toCustomClockText(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/**
 * Turn a calendar day (`YYYY-MM-DD`) plus a time selection into the UTC ms the
 * replay engine anchors on. Null when the date itself is invalid.
 */
export function resolveReplayStart(dateStr: string, selection: ReplayTimeSelection): number | null {
  const { hour, minute } = clockOf(selection)
  return exchangeWallTimeToUtcMs(dateStr, hour, minute)
}
